import {
  Badge,
  Box,
  Flex,
  HStack,
  Progress,
  Text,
  Tooltip,
  useColorModeValue,
  VStack,
} from '@chakra-ui/react';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface CurrentUsageTabProps {
  usageData: any;
  limitation?: {
    storage_gb: number | null;
    question: number | null;
    translation: number | null;
    expiration: string | null;
  };
}

const CurrentUsageTab: React.FC<CurrentUsageTabProps> = ({ usageData, limitation }) => {
  const { t } = useTranslation();

  const colorText = useColorModeValue('gray.800', 'white');
  const colorLabel = useColorModeValue('gray.600', 'gray.300');

  // Function to get color scheme for Chakra UI Progress component
  const getProgressColorScheme = (percentage: number): string => {
    if (percentage < 80) return 'green';
    if (percentage >= 80 && percentage < 90) return 'yellow';
    return 'red';
  };

  return (
    <VStack align='stretch' spacing={6}>
      {/* Question Usage */}
      <Box
        p={4}
        bg={useColorModeValue('gray.50', 'gray.700')}
        border='1px solid'
        borderColor={useColorModeValue('gray.200', 'gray.600')}
        borderRadius='md'
      >
        <HStack justify='space-between' mb={2}>
          <Text color={colorLabel}>{t('managementSystem.companyDetail.questionUsage')}</Text>
          <Flex align='center' gap={2}>
            <Text color={colorText} fontSize='lg' fontWeight='bold'>
              {usageData?.current_usage?.total_questions || 0}
            </Text>
            <Text color={colorText}>/</Text>
            {limitation?.question ? (
              <Text color={colorText}>
                {limitation.question} {t('managementSystem.companyDetail.questionsPerMonth')}
              </Text>
            ) : (
              <Badge fontSize='sm' colorScheme='green' variant='subtle'>
                {t('managementSystem.unlimited')}
              </Badge>
            )}
          </Flex>
        </HStack>
        {limitation?.question ? (
          <Tooltip
            label={`${
              limitation?.question && usageData?.current_usage?.total_questions
                ? Math.round((usageData.current_usage.total_questions / limitation.question) * 100)
                : 0
            }%`}
            placement='top'
          >
            <Progress
              cursor='pointer'
              colorScheme={getProgressColorScheme(
                limitation?.question && usageData?.current_usage?.total_questions
                  ? (usageData.current_usage.total_questions / limitation.question) * 100
                  : 0
              )}
              size='lg'
              value={
                limitation?.question && usageData?.current_usage?.total_questions
                  ? (usageData.current_usage.total_questions / limitation.question) * 100
                  : 0
              }
            />
          </Tooltip>
        ) : (
          <Text color={colorText} fontSize='sm' fontStyle='italic'>
            {t('managementSystem.unlimited')}
          </Text>
        )}
      </Box>

      {/* Storage Usage */}
      <Box
        p={4}
        bg={useColorModeValue('gray.50', 'gray.700')}
        border='1px solid'
        borderColor={useColorModeValue('gray.200', 'gray.600')}
        borderRadius='md'
      >
        <HStack justify='space-between' mb={2}>
          <Text color={colorLabel}>{t('managementSystem.companyDetail.storageUsage')}</Text>
          <Flex align='center' gap={2}>
            <Text color={colorText} fontSize='lg' fontWeight='bold'>
              {Number(usageData?.current_usage?.total_storage_gb || 0)
                .toFixed(2)
                .replace(/\.?0+$/, '')}
              GB
            </Text>
            <Text color={colorText}>/</Text>
            {typeof limitation?.storage_gb === 'number' ? (
              <Text color={colorText}>
                {limitation.storage_gb % 1 === 0
                  ? limitation.storage_gb.toString()
                  : Number(limitation.storage_gb)
                      .toFixed(2)
                      .replace(/\.?0+$/, '')}
                GB
              </Text>
            ) : (
              <Badge fontSize='sm' colorScheme='green' variant='subtle'>
                {t('managementSystem.unlimited')}
              </Badge>
            )}
          </Flex>
        </HStack>
        {limitation?.storage_gb ? (
          <Tooltip
            label={`${
              limitation?.storage_gb && usageData?.current_usage?.total_storage_gb
                ? Math.round(
                    (usageData.current_usage.total_storage_gb / limitation.storage_gb) * 100
                  )
                : 0
            }%`}
            placement='top'
          >
            <Progress
              cursor='pointer'
              colorScheme={getProgressColorScheme(
                limitation?.storage_gb && usageData?.current_usage?.total_storage_gb
                  ? (usageData.current_usage.total_storage_gb / limitation.storage_gb) * 100
                  : 0
              )}
              size='lg'
              value={
                limitation?.storage_gb && usageData?.current_usage?.total_storage_gb
                  ? (usageData.current_usage.total_storage_gb / limitation.storage_gb) * 100
                  : 0
              }
            />
          </Tooltip>
        ) : (
          <Text color={colorText} fontSize='sm' fontStyle='italic'>
            {t('managementSystem.unlimited')}
          </Text>
        )}
      </Box>

      {/* Translation Usage */}
      <Box
        p={4}
        bg={useColorModeValue('gray.50', 'gray.700')}
        border='1px solid'
        borderColor={useColorModeValue('gray.200', 'gray.600')}
        borderRadius='md'
      >
        <HStack justify='space-between' mb={2}>
          <Text color={colorLabel}>{t('managementSystem.companyDetail.translationUsage')}</Text>
          <Flex align='center' gap={2}>
            <Text color={colorText} fontSize='lg' fontWeight='bold'>
              {usageData?.current_usage?.total_translations || 0}
            </Text>
            <Text color={colorText}>/</Text>
            {limitation?.translation ? (
              <Text color={colorText}>
                {limitation.translation} {t('managementSystem.companyDetail.documentsPerMonth')}
              </Text>
            ) : (
              <Badge fontSize='sm' colorScheme='green' variant='subtle'>
                {t('managementSystem.unlimited')}
              </Badge>
            )}
          </Flex>
        </HStack>
        {limitation?.translation ? (
          <Tooltip
            label={`${
              limitation?.translation && usageData?.current_usage?.total_translations
                ? Math.round(
                    (usageData.current_usage.total_translations / limitation.translation) * 100
                  )
                : 0
            }%`}
            placement='top'
          >
            <Progress
              cursor='pointer'
              colorScheme={getProgressColorScheme(
                limitation?.translation && usageData?.current_usage?.total_translations
                  ? (usageData.current_usage.total_translations / limitation.translation) * 100
                  : 0
              )}
              size='lg'
              value={
                limitation?.translation && usageData?.current_usage?.total_translations
                  ? (usageData.current_usage.total_translations / limitation.translation) * 100
                  : 0
              }
            />
          </Tooltip>
        ) : (
          <Text color={colorText} fontSize='sm' fontStyle='italic'>
            {t('managementSystem.unlimited')}
          </Text>
        )}
      </Box>

      {/* Expiration Date */}
      <Box
        p={4}
        bg={useColorModeValue('gray.50', 'gray.700')}
        border='1px solid'
        borderColor={useColorModeValue('gray.200', 'gray.600')}
        borderRadius='md'
      >
        <HStack justify='space-between'>
          <Text color={colorLabel}>{t('managementSystem.companyDetail.expirationDate')}</Text>
          <Flex align='center' gap={2}>
            {limitation?.expiration ? (
              <Text color={colorText}>
                {new Date(limitation.expiration).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                })}
              </Text>
            ) : (
              <Badge fontSize='sm' colorScheme='green' variant='subtle'>
                {t('managementSystem.unlimited')}
              </Badge>
            )}
          </Flex>
        </HStack>
      </Box>
    </VStack>
  );
};

export default CurrentUsageTab;
